// PIQ Advocate Engine - Opus 4.5 Build
// Payouts Page - Payout queue management with bulk actions

import React, { useMemo, useState } from 'react'
import { useLocation } from 'wouter'
import { 
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Download,
  RefreshCw,
  Check,
  AlertCircle
} from 'lucide-react'
import { 
  usePayouts,
  useProcessPayouts,
  useApprovePayouts,
  useMarkPayoutsPaid,
  type AdvocatePayout
} from '../../lib/advocate-opus-data-service'

// ============================================================================
// COMPONENT
// ============================================================================

export default function AdvocateOpusPayouts() {
  const [location, navigate] = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(location.split('?')[1] || ''), [location])
  
  const [statusFilter, setStatusFilter] = useState<string>(
    searchParams.get('status') || 'pending'
  )
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showPayModal, setShowPayModal] = useState(false)
  
  const { data, isLoading, refetch } = usePayouts({ 
    status: statusFilter || undefined,
    eligible_only: statusFilter === 'pending' ? true : undefined,
  })
  
  const processPayouts = useProcessPayouts()
  const approvePayouts = useApprovePayouts()
  const markPaid = useMarkPayoutsPaid()

  const handleSelectAll = () => {
    if (selectedIds.size === data?.payouts?.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(data?.payouts?.map((p: any) => p.payout_id) || []))
    }
  }

  const handleSelect = (id: string) => {
    const newSet = new Set(selectedIds)
    if (newSet.has(id)) {
      newSet.delete(id)
    } else {
      newSet.add(id)
    }
    setSelectedIds(newSet)
  }

  const handleApproveSelected = async () => {
    await approvePayouts.mutateAsync(Array.from(selectedIds))
    setSelectedIds(new Set())
    refetch()
  }

  const handleProcessEligible = async () => {
    await processPayouts.mutateAsync()
    refetch()
  }

  const payouts = data?.payouts || []
  const summary = data?.summary || {}
  const pendingLiability = data?.pending_liability || 0

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-8 py-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">Payout Management</h1>
                <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                  Opus Build
                </span>
              </div>
              <p className="text-gray-500 mt-1">Review and process advocate commission payouts</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleProcessEligible}
                disabled={processPayouts.isPending}
                className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${processPayouts.isPending ? 'animate-spin' : ''}`} />
                Process Eligible
              </button>
              <button
                onClick={() => window.open(`/api/advocate-opus/payouts?action=export&status=approved`, '_blank')}
                className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
            <SummaryCard
              label="Pending"
              count={summary.pending?.count || 0}
              amount={summary.pending?.total_amount || 0}
              icon={Clock}
              color="yellow"
              onClick={() => setStatusFilter('pending')}
              active={statusFilter === 'pending'}
            />
            <SummaryCard
              label="Approved"
              count={summary.approved?.count || 0}
              amount={summary.approved?.total_amount || 0}
              icon={CheckCircle}
              color="green"
              onClick={() => setStatusFilter('approved')}
              active={statusFilter === 'approved'}
            />
            <SummaryCard
              label="Paid"
              count={summary.paid?.count || 0}
              amount={summary.paid?.total_amount || 0}
              icon={DollarSign}
              color="blue"
              onClick={() => setStatusFilter('paid')}
              active={statusFilter === 'paid'}
            />
            <SummaryCard
              label="Cancelled"
              count={summary.cancelled?.count || 0}
              amount={summary.cancelled?.total_amount || 0}
              icon={XCircle}
              color="gray"
              onClick={() => setStatusFilter('cancelled')}
              active={statusFilter === 'cancelled'}
            />
          </div>

          {/* Pending Liability Alert */}
          {pendingLiability > 0 && statusFilter === 'pending' && (
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-800">
                  Pending Liability: ${pendingLiability.toLocaleString()}
                </p>
                <p className="text-sm text-yellow-600">
                  Payouts eligible for approval after return window
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-8 py-6">
        {/* Bulk Actions */}
        {selectedIds.size > 0 && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4 flex items-center justify-between">
            <span className="text-purple-700 font-medium">
              {selectedIds.size} payout(s) selected
            </span>
            <div className="flex gap-3">
              {statusFilter === 'pending' && (
                <button
                  onClick={handleApproveSelected}
                  disabled={approvePayouts.isPending}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  Approve Selected
                </button>
              )}
              {statusFilter === 'approved' && (
                <button
                  onClick={() => setShowPayModal(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Mark as Paid
                </button>
              )}
              <button
                onClick={() => setSelectedIds(new Set())}
                className="px-4 py-2 border rounded-lg hover:bg-white"
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {/* Payouts Table */}
        {isLoading ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : payouts.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <DollarSign className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No {statusFilter} payouts</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === payouts.length && payouts.length > 0}
                      onChange={handleSelectAll}
                      className="rounded"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Advocate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Commission</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Eligible Date</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {payouts.map((payout: any) => (
                  <tr key={payout.payout_id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(payout.payout_id)}
                        onChange={() => handleSelect(payout.payout_id)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <div 
                        className="cursor-pointer hover:text-purple-600"
                        onClick={() => navigate(`/advocate-opus/advocates/${payout.advocate_id}`)}
                      >
                        <div className="font-medium">@{payout.primary_handle}</div>
                        <div className="text-sm text-gray-500 capitalize">{payout.primary_platform}</div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm">
                        <div className="font-mono">{payout.order_id?.slice(0, 12)}...</div>
                        <div className="text-gray-500">
                          {payout.order_date ? new Date(payout.order_date).toLocaleDateString() : '-'}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="font-medium">${payout.gross_revenue?.toLocaleString() || '0'}</div>
                      {payout.refunded && (
                        <div className="text-xs text-red-500">Refunded</div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="font-medium text-green-600">
                        ${payout.final_commission?.toLocaleString() || '0'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {payout.commission_rate}% {payout.commission_type}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <StatusBadge status={payout.status} />
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      {payout.eligible_date ? new Date(payout.eligible_date).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mark Paid Modal */}
      {showPayModal && (
        <MarkPaidModal
          count={selectedIds.size}
          onConfirm={async (method, reference) => {
            await markPaid.mutateAsync({
              payout_ids: Array.from(selectedIds),
              payment_method: method,
              payment_reference: reference,
            })
            setSelectedIds(new Set())
            setShowPayModal(false)
            refetch()
          }}
          onClose={() => setShowPayModal(false)}
          isPending={markPaid.isPending}
        />
      )}
    </div>
  )
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface SummaryCardProps {
  label: string
  count: number
  amount: number
  icon: React.ElementType
  color: 'yellow' | 'green' | 'blue' | 'gray'
  onClick: () => void
  active: boolean
}

function SummaryCard({ label, count, amount, icon: Icon, color, onClick, active }: SummaryCardProps) {
  const colors = {
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
  }

  const iconColors = {
    yellow: 'text-yellow-600',
    green: 'text-green-600',
    blue: 'text-blue-600',
    gray: 'text-gray-600',
  }

  return (
    <button
      onClick={onClick}
      className={`p-4 rounded-xl border-2 text-left transition-all ${
        active ? colors[color] : 'bg-white border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-center gap-3">
        <Icon className={`w-5 h-5 ${active ? iconColors[color] : 'text-gray-400'}`} />
        <span className="font-medium">{label}</span>
      </div>
      <div className="mt-2">
        <span className="text-2xl font-bold">{count}</span>
        <span className="text-sm ml-2 text-gray-500">${amount.toLocaleString()}</span>
      </div>
    </button>
  )
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-green-100 text-green-700',
    reduced: 'bg-orange-100 text-orange-700',
    paid: 'bg-blue-100 text-blue-700',
    cancelled: 'bg-gray-100 text-gray-700',
    failed: 'bg-red-100 text-red-700',
  }

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded capitalize ${styles[status] || styles.pending}`}>
      {status}
    </span>
  )
}

interface MarkPaidModalProps {
  count: number
  onConfirm: (method: string, reference?: string) => void
  onClose: () => void
  isPending: boolean
}

function MarkPaidModal({ count, onConfirm, onClose, isPending }: MarkPaidModalProps) {
  const [method, setMethod] = useState('bank_transfer')
  const [reference, setReference] = useState('')

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Mark {count} Payout(s) as Paid
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="bank_transfer">Bank Transfer</option>
              <option value="paypal">PayPal</option>
              <option value="check">Check</option>
              <option value="store_credit">Store Credit</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Reference (optional)
            </label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Transaction ID, check number, etc."
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(method, reference || undefined)}
            disabled={isPending}
            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {isPending ? 'Processing...' : 'Confirm Payment'}
          </button>
        </div>
      </div>
    </div>
  )
}
